import React, { PropsWithChildren, useMemo, useState } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import API from 'api/API'
import {CardServices, CorporationMember, PersonNames, UserData} from 'api/Models'
import iconStolen from 'assets/imgs/icons/report-stolen.svg'
import iconClose from 'assets/imgs/icons/close-card.svg'
import iconEdit from 'assets/imgs/icons/edit.svg'
import { toast } from 'react-toastify'
import {defaultToastOptions, formatPersonNames } from 'utils'
import { CircleSpinnerOverlay } from 'react-spinner-overlay'
import { Col} from 'react-bootstrap'
import { CardData } from 'api/Models'
import Label from 'components/elements/Label'
import BasePage from 'components/pages/BasePage'

interface UserViewProps {
	userName: PersonNames
}

function UserView(props: UserViewProps) {
	return (
		<>
			<div className="user-info">
				<div className="user-icon">{GetFirstLetter(props.userName.first)}</div>
				<div className="user-name">{formatPersonNames(props.userName)}</div>
			</div>
		</>
	)
}

function GetFirstLetter(name: string | null | undefined) {
	if (name === undefined || name === null || name.length === 0) {
		return '?'
	}

	return name[0].toUpperCase()
}

function StatusTag(props: { status: string | null }) {
	switch (props.status) {
		case 'Active':
			return <div className="status active">Active</div>
		case 'Inactive':
			return <div className="status inactive">Inactive</div>
		case 'BlockedByClient':
			return <div className="status inactive">Frozen</div>
		case 'Stolen':
			return <div className="status inactive">Reported Stolen</div>
		case 'Lost':
			return <div className="status inactive">Reported Lost</div>
		case 'Closed':
			return <div className="status inactive">Closed</div>
		default:
			return <div className="status pending">{props.status}</div>
	}
}

interface CardControlsProps {
	cardId: string
	cardStatus: string | null
	setBusy: (Value: boolean) => void
	updateStatus: (status: string) => void
}

function CardControls(props: CardControlsProps) {
	const freezeCard = async () => {
		props.setBusy(true)
		const result = await API.Cards.Freeze(props.cardId)
		if (result.success) {
			props.updateStatus('BlockedByCustomer')
		}
		props.setBusy(false)
	}

	const unfreezeCard = async () => {
		props.setBusy(true)
		const result = await API.Cards.Unfreeze(props.cardId)
		if (result.success) {
			props.updateStatus('Active')
		} else {
			toast.error('Failed to unfreeze card: ' + result.error, defaultToastOptions())
			console.log('Error', result)
		}

		props.setBusy(false)
	}

	const reportStolen = async () => {
		const confirm = window.confirm('Report stolen? This will block the card. This is not reversible.')
		if (!confirm) {
			return
		}
		props.setBusy(true)
		const result = await API.Cards.ReportStolen(props.cardId)
		if (result.success) {
			props.updateStatus('Stolen')
		}
		props.setBusy(false)
	}

	return (
		<div className="block-nav">
			<div className="row">
				{/*<div className="col">*/}
				{/*    <button>*/}
				{/*        <span className="nav-icn"><img src="assets/imgs/icons/show-details.svg" alt=""/></span>*/}
				{/*        <span className="nav-link">Show details</span>*/}
				{/*    </button>*/}
				{/*</div>*/}

				{props.cardStatus === 'Active' && (
					<div className="col">
						<button onClick={freezeCard}>
							<div className="nav-icn">
								<img src={iconStolen} alt="" />
							</div>
							<div className="nav-link">Freeze Cards</div>
						</button>
					</div>
				)}
				{(props.cardStatus === 'BlockedByCustomer' ||
					props.cardStatus === 'Closed' ||
					props.cardStatus === 'Stolen') && (
					<div className="col">
						<button onClick={unfreezeCard}>
							<div className="nav-icn">
								<img src={iconStolen} alt="" />
							</div>
							<div className="nav-link">Unfreeze Cards</div>
						</button>
					</div>
				)}
				<div className="col">
					<button onClick={reportStolen}>
						<div className="nav-icn">
							<img src={iconStolen} alt="" />
						</div>
						<div className="nav-link">Report Stolen</div>
					</button>
				</div>
				<div className="col">
					<button>
						<div className="nav-icn red">
							<img src={iconClose} alt="" />
						</div>
						<div className="nav-link red">Close card</div>
					</button>
				</div>
			</div>
		</div>
	)
}

interface InfoEntryProps {
	label: string;
	value: any;
	editable?: boolean
}

function InfoEntry(props: InfoEntryProps) {
	return (
		<li>
			<div className="label">
				{props.value.toString()}
				<span> {props.label}</span>
			</div>
			{props.editable && (
				<div className="func">
					<div>
						<img src={iconEdit} alt="" />
					</div>
				</div>
			)}
		</li>
	)
}

function InfoPanel(props: { card: CardData }) {
	return (
		<>
			<div className="block block-cd-details">
				<div className="block-heading">Card details</div>
				<div className="func">
					<div>
						<img src={iconEdit} alt="" />
					</div>
				</div>
				<div className="block-list">
					<ul>
						<InfoEntry label={'Custom Name'} value={props.card.processorData?.embossName} />
						<InfoEntry label={'Linked user'} value={props.card.cardholderName} />
						<InfoEntry label={'Created on'} value={new Date(props.card.createdOn).toDateString()} />
						<InfoEntry label={'Modified on'} value={new Date(props.card.modifiedOn ?? 0).toDateString()} />
						<InfoEntry
							label={'Activated on'}
							value={
								props.card.activatedPlasticAt
									? new Date(props.card.activatedPlasticAt).toDateString()
									: 'Not activated'
							}
						/>
					</ul>
				</div>
			</div>
		</>
	)
}

function ServiceEntry(props: { label: string; desc?: string; value: boolean; onChange: (value: boolean) => void }) {
	return (
		<li>
			<Label>
				{props.label} {props.desc && <span>{props.desc}</span>}
			</Label>
			<div className="func">
				<label className="slide-button">
					<input
						type="checkbox"
						defaultChecked={props.value}
						onChange={(c) => props.onChange(c.target.checked)}
					/>
					<span className="slide-indicator"></span>
					<span className="slide-label"></span>
				</label>
			</div>
		</li>
	)
}

function ServicesPanel(props: { services: CardServices; onChange(services: CardServices): void }) {
	return (
		<>
			<div className="block">
				<div className="block-heading">Transaction type limits</div>
				<div className="block-list">
					<ul>
						<ServiceEntry
							label={'POS'}
							value={props.services.servicePOS}
							onChange={() =>
								props.onChange({
									...props.services,
									servicePOS: !props.services.servicePOS,
								})
							}
						/>
						<ServiceEntry
							label={'ATM'}
							value={props.services.serviceATM}
							onChange={() =>
								props.onChange({
									...props.services,
									serviceATM: !props.services.serviceATM,
								})
							}
						/>
						<ServiceEntry
							label={'Abroad'}
							value={props.services.serviceAbroad}
							onChange={() =>
								props.onChange({
									...props.services,
									serviceAbroad: !props.services.serviceAbroad,
								})
							}
						/>
						<ServiceEntry
							label={'Contactless'}
							value={props.services.serviceContactless}
							onChange={() =>
								props.onChange({
									...props.services,
									serviceContactless: !props.services.serviceContactless,
								})
							}
						/>
						<ServiceEntry
							label={'CNP'}
							value={props.services.serviceCNP}
							onChange={() =>
								props.onChange({
									...props.services,
									serviceCNP: !props.services.serviceCNP,
								})
							}
						/>
					</ul>
				</div>
			</div>
		</>
	)
}

export default function UserSettingsPage() {
	const routerParams = useParams()
	const corporationId = routerParams['corporationId']
	const userId = routerParams['userId']

	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(false)
	const [user, setUser] = useState<UserData | null>(null)
	const [corporationMember, setCorporationMember] = useState<CorporationMember | null>(null)
	const [cards, setCards] = useState<CardData[]>([])
	const [busy, setBusy] = useState(false)

	useMemo(() => {
		if (!userId || !corporationId) {
			return
		}

		setLoading(true)

		async function fetchData() {
			await API.Users.GetUser(userId!).then((r) => {
				if (r.success) {
					setUser(r.data!)
				} else {
					setError(true)
					toast.error('Failed to load user data', defaultToastOptions())
					return
				}
			})

			await API.Corporations.GetCorporationMember(corporationId!, userId!).then((r) => {
				if (r.success) {
					setCorporationMember(r.data!)
				} else {
					setError(true)
					toast.error('Failed to load user data', defaultToastOptions())
					return
				}
			})


			await API.Cards.GetCards({ 'Filters.UserId': userId }).then((r) => {
				if (r.data) {
					setCards(r.data.data)
				} else {
					setError(true)
					toast.error('Failed to load card data', defaultToastOptions())
					return
				}
			})
		}

		fetchData().then(() => setLoading(false))
	}, [userId, corporationId])

	if (error || !userId || !corporationId) {
		return <Navigate to={'/missing'} />
	}

	if (user === null) {
		return (
			<BasePage className="cd-management">
				<CircleSpinnerOverlay />
			</BasePage>
		)
	}



	return (
		<>
			{(busy || loading) && <CircleSpinnerOverlay />}
			<BasePage>
				<Col lg={4}>
					<UserBlock>
						<UserView userName={corporationMember?.personNames[0] ?? {first: "?", middle: "?", last: "?", language: "en"}} />
						<StatusTag status={user.status} />
					</UserBlock>
				</Col>
				<Col lg={4}>
					{/*<CardBalanceBlock>*/}
					{/*	<Label>Available funds</Label>*/}
					{/*	<Value>{formatCurrency(card.balance ?? 0, card.currency)} </Value>*/}
					{/*	<div className="to-time"> November 23, 2023 | 02:20 PM</div>*/}
					{/*</CardBalanceBlock>*/}
					{/*{cardServices && (*/}
					{/*	<ServicesPanel*/}
					{/*		services={cardServices}*/}
					{/*		onChange={async (services) => {*/}
					{/*			setCardServices(services)*/}
					{/*			await API.Cards.SetCardServicesTest(id as string, services)*/}
					{/*		}}*/}
					{/*	/>*/}
					{/*)}*/}
				</Col>
				{/*{cardLimits && (*/}
				{/*	<Col lg={4}>*/}
				{/*		/!*<LimitsBlock label={'Daily'} limit={cardLimits.daily} currency={card.currency} />*!/*/}
				{/*		/!*<LimitsBlock label={'Weekly'} limit={cardLimits.weekly} currency={card.currency} />*!/*/}
				{/*		/!*<LimitsBlock label={'Monthly'} limit={cardLimits.monthly} currency={card.currency} />*!/*/}
				{/*		<LimitsBlock cardLimits={cardLimits} currency={card.currency} />*/}
				{/*		<Button onClick={submitCardLimits}> Send New Limits</Button>*/}
				{/*	</Col>*/}
				{/*)}*/}
			</BasePage>
		</>
	)
}

function UserBlock(props: PropsWithChildren) {
	return <div className="block block-user">{props.children}</div>
}

// function CardBalanceBlock(props: PropsWithChildren) {
// 	return <div className="block block-card-balance">{props.children}</div>
// }
